import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../api';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [academyCode, setAcademyCode] = useState('');
  const [needsAcademyCode, setNeedsAcademyCode] = useState(false);
  const [currentAcademy, setCurrentAcademy] = useState('');
  const { login, getSavedAcademyCode, clearSavedAcademyCode } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // 저장된 학원코드 확인
    const savedCode = getSavedAcademyCode();
    if (savedCode && savedCode !== 'default') {
      setCurrentAcademy(savedCode);
      setNeedsAcademyCode(false);
    } else {
      setNeedsAcademyCode(true);
    }
  }, [getSavedAcademyCode]);

  const handleAcademySubmit = async (e) => {
    e.preventDefault();
    if (!academyCode || academyCode.length < 3) {
      setError('학원코드는 3자 이상이어야 합니다');
      return;
    }

    setVerifying(true);
    setError('');

    try {
      // 학원코드 존재 여부 확인
      const res = await api.get(`/api/auth/verify-academy-code/${academyCode.toLowerCase()}`);

      if (!res.data.exists) {
        setError(res.data.reason || '등록되지 않은 학원코드입니다');
        setVerifying(false);
        return;
      }

      // 학원코드 저장하고 로그인 화면으로 전환
      localStorage.setItem('academyCode', academyCode.toLowerCase());
      setCurrentAcademy(academyCode.toLowerCase());
      setNeedsAcademyCode(false);
    } catch (err) {
      setError('학원코드 확인에 실패했습니다');
    } finally {
      setVerifying(false);
    }
  };

  const handleChangeAcademy = () => {
    clearSavedAcademyCode();
    setCurrentAcademy('');
    setNeedsAcademyCode(true);
    setAcademyCode('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || '로그인에 실패했습니다');
    } finally {
      setLoading(false);
    }
  };

  // 학원코드 입력 화면
  if (needsAcademyCode) {
    return (
      <div className="login-page">
        <div className="login-container">
          <div className="login-header">
            <h1>EduStaff</h1>
            <p>학원 관리 시스템</p>
          </div>

          <form onSubmit={handleAcademySubmit} className="login-form">
            {error && <div className="error-message">{error}</div>}

            <div className="academy-code-notice">
              <p>로그인하려면 학원코드를 입력하세요</p>
            </div>

            <div className="form-group">
              <label htmlFor="academyCode">학원코드</label>
              <input
                id="academyCode"
                type="text"
                value={academyCode}
                onChange={(e) => setAcademyCode(e.target.value.toLowerCase().replace(/[^a-z0-9]/g, ''))}
                placeholder="예: myacademy"
                required
                autoFocus
                maxLength={20}
              />
              <small className="input-hint">회원가입 시 설정한 학원코드를 입력하세요</small>
            </div>

            <button type="submit" className="login-btn" disabled={verifying}>
              {verifying ? '확인 중...' : '다음'}
            </button>
          </form>

          <div className="login-footer">
            <p>아직 학원을 등록하지 않으셨나요? <Link to="/register">무료로 시작하기</Link></p>
          </div>
        </div>
      </div>
    );
  }

  // 로그인 화면
  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <h1>EduStaff</h1>
          <p>학원 관리 시스템</p>
        </div>

        {currentAcademy && (
          <div className="current-academy-badge">
            <span className="academy-name">{currentAcademy}</span>
            <button type="button" className="change-academy-btn" onClick={handleChangeAcademy}>
              변경
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit} className="login-form">
          {error && <div className="error-message">{error}</div>}

          <div className="form-group">
            <label htmlFor="email">이메일</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="이메일을 입력하세요"
              required
              autoFocus
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">비밀번호</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="비밀번호를 입력하세요"
              required
            />
          </div>

          <button type="submit" className="login-btn" disabled={loading}>
            {loading ? '로그인 중...' : '로그인'}
          </button>
        </form>

        <div className="login-footer">
          <p>아직 학원을 등록하지 않으셨나요? <Link to="/register">무료로 시작하기</Link></p>
        </div>
      </div>
    </div>
  );
}
